import { Injectable } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import {
  CommercialFacility,
  CommercialFacilityConnection,
  CommercialFacilityNote,
} from 'src/app/model/commercial-services/commercial-facility';
import { CommercialFacilityUpdateDto } from 'src/app/model/commercial-services/commercial-facility-update-dto';
import { getCorrectedDate, isNullOrUndefined } from 'src/app/utils/utils';
import {
  CommercialFacilityMessageKeys,
  ValidationConfig,
} from 'src/app/core/type-helpers/validation-msg';
import { CommercialContactBuilderService } from './commercial-contact-builder.service';
import { inject } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class CommercialFacilityBuilderService {
  private _commercialContactBuilder = inject(CommercialContactBuilderService);

  buildCommercialFacilityValidationMessages(): ValidationConfig<CommercialFacilityMessageKeys> {
    return {
      messages: {
        facilityName: {
          required: 'COMMERCIAL_SERVICES.VALIDATION.FACILITY_NAME_REQUIRED',
        },
        facilityId: {
          required: 'COMMERCIAL_SERVICES.VALIDATION.FACILITY_ID_REQUIRED',
        },
        companyId: {
          required: 'COMMERCIAL_SERVICES.VALIDATION.COMPANY_REQUIRED',
        },
        facilityTypeId: {
          required: 'COMMERCIAL_SERVICES.VALIDATION.FACILITY_TYPE_REQUIRED',
        },
        operatingAreaId: {
          required: 'COMMERCIAL_SERVICES.VALIDATION.OPERATING_AREA_REQUIRED',
        },
        marketAreaId: {
          required: 'COMMERCIAL_SERVICES.VALIDATION.MARKET_AREA_REQUIRED',
        },
        aorId: {
          required: 'COMMERCIAL_SERVICES.VALIDATION.AOR_REQUIRED',
        },
        numberOfUnits: {
          min: 'COMMERCIAL_SERVICES.VALIDATION.NUMBER_OF_UNITS_INVALID',
        },
      },
      params: {},
    };
  }

  buildNewCommercialFacility(): CommercialFacility {
    return {
      id: '',
      companyId: null,
      facilityName: '',
      facilityTypeId: null,
      facilityId: '',
      mcFacilityId: null,
      marketAreaId: null,
      facilityLineSegment: null,
      aorId: null,
      numberOfUnits: null,
      owner: null,
      operator: null,
      fuelManager: null,
      balancingTypeId: null,
      contract: null,
      rateableTake: false,
      criticalDay: false,
      createdAt: '',
      createdBy: '',
      updatedAt: '',
      updater: '',
      version: 0,
      company: null,
      facilityType: null,
      operatingArea: null,
      marketArea: null,
      aor: null,
      gcSupervisor: null,
      gcManager: null,
      balancingType: null,
      Contacts: [],
      Notes: [],
    };
  }

  buildCommercialFacility(item: any): CommercialFacility | null {
    if (isNullOrUndefined(item)) {
      return null;
    }

    const buildContactSummary = (contact: any) =>
      contact
        ? {
            id: contact.id,
            firstName: contact.firstName ?? '',
            lastName: contact.lastName ?? '',
            fullName:
              `${contact.firstName ?? ''} ${contact.lastName ?? ''}`.trim(),
          }
        : null;

    return {
      id: item.id,
      companyId: item.companyId,
      facilityName: item.facilityName,
      facilityTypeId: item.facilityTypeId,
      facilityId: item.facilityId,
      mcFacilityId: item.mcFacilityId,
      marketAreaId: item.marketAreaId,
      facilityLineSegment: item.facilityLineSegment,
      aorId: item.aorId,
      numberOfUnits: item.numberOfUnits,
      owner: item.owner,
      operator: item.operator,
      fuelManager: item.fuelManager,
      balancingTypeId: item.balancingTypeId,
      contract: item.contract,
      rateableTake: item.rateableTake,
      criticalDay: item.criticalDay,
      createdAt: item.createdAt,
      createdBy: item.createdBy,
      updatedAt: item.updatedAt,
      updater: item.updater,
      deletedAt: item.deletedAt,
      version: item.version,
      company: item.Company ?? null,
      facilityType: item.FacilityType ?? null,
      operatingArea: item.OperatingArea ?? null,
      marketArea: item.MarketArea ?? null,
      aor: item.AOR ?? null,
      gcSupervisor: buildContactSummary(item.gcSupervisor),
      gcManager: buildContactSummary(item.gcManager),
      balancingType: item.BalancingType ?? null,
      Contacts: (item.Contacts ?? [])
        .map((contact: any) =>
          this._commercialContactBuilder.buildCommercialContact(contact)
        )
        .filter((contact: any) => contact !== null),
      Notes: (item.Notes ?? []).map((note: any) =>
        this.buildCommercialFacilityNote(note)
      ),
    };
  }

  buildCommercialFacilityNote(item: any): CommercialFacilityNote {
    const createdAt = getCorrectedDate(item.createdAt)!;
    const updatedAt = getCorrectedDate(item.updatedAt)!;
    return {
      id: item.id,
      commercialFacilityId: item.commercialFacilityId,
      note: item.note ?? '',
      createdAt,
      createdBy: item.createdBy,
      updatedAt,
      updater: item.updater,
      deletedAt: getCorrectedDate(item.deletedAt),
      version: item.version,
      time: updatedAt ?? createdAt,
      user: item.updater || item.createdBy,
    };
  }

  buildCommercialFacilityConnection(item: any): CommercialFacilityConnection {
    return {
      items: item?.items ?? [],
      totalRecords: item?.totalRecords ?? 0,
    };
  }

  buildCommercialFacilityQuery(filters: any): Record<string, string> {
    const query: Record<string, string> = {};

    Object.keys(filters ?? {}).forEach((key) => {
      const filter = filters[key];
      const value = Array.isArray(filter) ? filter[0]?.value : filter?.value;
      if (value !== null && value !== undefined && value !== '') {
        query[key] = typeof value === 'string' ? value.trim() : String(value);
      }
    });

    return query;
  }

  buildForm(
    fb: UntypedFormBuilder,
    facility: CommercialFacility
  ): UntypedFormGroup {
    return fb.group({
      id: [facility.id],
      companyId: [facility.companyId, Validators.required],
      facilityName: [facility.facilityName, Validators.required],
      facilityTypeId: [facility.facilityTypeId, Validators.required],
      facilityId: [facility.facilityId, Validators.required],
      mcFacilityId: [facility.mcFacilityId],
      operatingAreaId: [
        facility.operatingArea?.id ?? null,
        Validators.required,
      ],
      marketAreaId: [facility.marketAreaId, Validators.required],
      facilityLineSegment: [facility.facilityLineSegment],
      aorId: [facility.aorId, Validators.required],
      numberOfUnits: [facility.numberOfUnits, Validators.min(0)],
      owner: [facility.owner],
      operator: [facility.operator],
      fuelManager: [facility.fuelManager],
      balancingTypeId: [facility.balancingTypeId],
      contract: [facility.contract],
      rateableTake: [facility.rateableTake],
      criticalDay: [facility.criticalDay],
      commercialContactIds: [
        facility.Contacts?.map((contact) => contact.id) ?? [],
      ],
      version: [facility.version],
    });
  }

  updateForm(
    form: UntypedFormGroup,
    facility: CommercialFacility,
    writeAccess: boolean
  ): void {
    form.patchValue({
      id: facility.id,
      companyId: facility.companyId,
      facilityName: facility.facilityName,
      facilityTypeId: facility.facilityTypeId,
      facilityId: facility.facilityId,
      mcFacilityId: facility.mcFacilityId,
      operatingAreaId: facility.operatingArea?.id ?? null,
      marketAreaId: facility.marketAreaId,
      facilityLineSegment: facility.facilityLineSegment,
      aorId: facility.aorId,
      numberOfUnits: facility.numberOfUnits,
      owner: facility.owner,
      operator: facility.operator,
      fuelManager: facility.fuelManager,
      balancingTypeId: facility.balancingTypeId,
      contract: facility.contract,
      rateableTake: facility.rateableTake,
      criticalDay: facility.criticalDay,
      commercialContactIds:
        facility.Contacts?.map((contact) => contact.id) ?? [],
      version: facility.version,
    });
    form.markAsPristine();
    form.markAsUntouched();

    if (!writeAccess) {
      form.disable();
    }
  }

  buildCreateObject(facility: any): CommercialFacilityUpdateDto {
    return {
      companyId: facility.companyId,
      facilityName: facility.facilityName?.trim(),
      facilityTypeId: facility.facilityTypeId,
      facilityId: facility.facilityId?.trim(),
      mcFacilityId: facility.mcFacilityId || null,
      marketAreaId: facility.marketAreaId,
      facilityLineSegment: this.nullableText(facility.facilityLineSegment),
      aorId: facility.aorId,
      numberOfUnits: facility.numberOfUnits ?? null,
      owner: this.nullableText(facility.owner),
      operator: this.nullableText(facility.operator),
      fuelManager: this.nullableText(facility.fuelManager),
      balancingTypeId: facility.balancingTypeId || null,
      contract: this.nullableText(facility.contract),
      rateableTake: facility.rateableTake,
      criticalDay: facility.criticalDay,
      commercialContactIds: facility.commercialContactIds ?? [],
    };
  }

  buildUpdateStatement(
    facility: CommercialFacility,
    changes: any
  ): CommercialFacilityUpdateDto {
    const update: CommercialFacilityUpdateDto = {
      expectedVersion: facility.version + 1,
    };

    Object.keys(changes ?? {}).forEach((key) => {
      // operatingAreaId only filters the market area dropdown; the API derives it.
      if (key !== 'operatingAreaId' && changes[key] !== undefined) {
        (update as any)[key] = changes[key];
      }
    });

    return update;
  }

  private nullableText(value: string | null | undefined): string | null {
    const trimmed = value?.trim();
    return trimmed ? trimmed : null;
  }
}
